import { useMemo } from 'react';
import { useTransactions } from '../useTransactions.ts';
import type { AccountMetrics, TransactionFilters } from '@/types/account-drilldown.ts';

// Session storage key for metrics cache
const METRICS_CACHE_KEY = 'account_metrics_cache';

interface CachedMetrics {
  accountId: string;
  filters: TransactionFilters;
  metrics: AccountMetrics;
  timestamp: number;
}

// Helper to generate cache key from filters
const getCacheKey = (accountId: string, filters: TransactionFilters): string => {
  return `${accountId}_${filters.timePeriod}_${filters.categoryId || 'all'}_${filters.dateFrom || ''}_${filters.dateTo || ''}`;
};

// Load cached metrics from session storage
const loadCachedMetrics = (accountId: string, filters: TransactionFilters): AccountMetrics | null => {
  try {
    const cacheKey = getCacheKey(accountId, filters);
    const cached = sessionStorage.getItem(`${METRICS_CACHE_KEY}_${cacheKey}`);
    
    if (!cached) return null;

    const parsed: CachedMetrics = JSON.parse(cached);
    
    // Return cached metrics (no expiration for session cache)
    return parsed.metrics;
  } catch (error) {
    console.error('Error loading cached metrics:', error);
    return null;
  }
};

// Save metrics to session storage
const saveCachedMetrics = (
  accountId: string,
  filters: TransactionFilters,
  metrics: AccountMetrics
): void => {
  try {
    const cacheKey = getCacheKey(accountId, filters);
    const cached: CachedMetrics = {
      accountId,
      filters,
      metrics,
      timestamp: Date.now(),
    };
    
    sessionStorage.setItem(
      `${METRICS_CACHE_KEY}_${cacheKey}`,
      JSON.stringify(cached)
    );
  } catch (error) {
    console.error('Error saving cached metrics:', error);
  }
};

/**
 * Calculate account metrics from transactions
 * Pure function for easy testing
 */
export function calculateAccountMetrics(
  transactions: Array<{ type: string; amount: number }>
): AccountMetrics {
  let totalIncome = 0;
  let totalExpenses = 0;
  let transactionCount = 0;

  for (const tx of transactions) {
    transactionCount++;
    
    if (tx.type === 'income') {
      totalIncome += tx.amount;
    } else if (tx.type === 'expense') {
      totalExpenses += tx.amount;
    }
  }

  const netChange = totalIncome - totalExpenses;
  const averageTransaction = transactionCount > 0 
    ? (totalIncome + totalExpenses) / transactionCount 
    : 0;

  return {
    totalIncome,
    totalExpenses,
    netChange,
    transactionCount,
    averageTransaction,
  };
}

/**
 * Hook to calculate and cache account metrics for a given time period
 * 
 * @param accountId - The UUID of the account
 * @param filters - Transaction filters (time period, category, date range)
 * @returns Calculated metrics with loading state and error
 */
export function useAccountMetrics(accountId: string, filters: TransactionFilters) {
  // Build transaction query filters
  const transactionFilters = useMemo(() => {
    const queryFilters: {
      accountId: string;
      categoryId?: string;
      dateFrom?: string;
      dateTo?: string;
    } = {
      accountId,
    };

    // Add category filter if specified
    if (filters.categoryId) {
      queryFilters.categoryId = filters.categoryId;
    }

    // Calculate date range based on time period
    const now = new Date();
    const today = now.toISOString().split('T')[0];

    switch (filters.timePeriod) {
      case 'last7days': {
        const date = new Date(now);
        date.setDate(date.getDate() - 7);
        queryFilters.dateFrom = date.toISOString().split('T')[0];
        queryFilters.dateTo = today;
        break;
      }
      case 'last30days': {
        const date = new Date(now);
        date.setDate(date.getDate() - 30);
        queryFilters.dateFrom = date.toISOString().split('T')[0];
        queryFilters.dateTo = today;
        break;
      }
      case 'last90days': {
        const date = new Date(now);
        date.setDate(date.getDate() - 90);
        queryFilters.dateFrom = date.toISOString().split('T')[0];
        queryFilters.dateTo = today;
        break;
      }
      case 'custom':
        if (filters.dateFrom) queryFilters.dateFrom = filters.dateFrom;
        if (filters.dateTo) queryFilters.dateTo = filters.dateTo;
        break;
      case 'alltime':
        // No date filters for all time
        break;
    }

    return queryFilters;
  }, [accountId, filters]);

  // Fetch transactions with filters
  const { data: transactions, isLoading, error } = useTransactions(transactionFilters);

  // Calculate metrics with memoization
  const metrics = useMemo(() => {
    if (!transactions) return undefined;

    // Check cache first
    const cached = loadCachedMetrics(accountId, filters);
    if (cached) {
      return cached;
    }

    // Calculate metrics
    const calculated = calculateAccountMetrics(transactions);

    // Save to cache
    saveCachedMetrics(accountId, filters, calculated);

    return calculated;
  }, [transactions, accountId, filters]);

  return {
    metrics,
    isLoading,
    error: error as Error | null,
  };
}
